import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Header from '../../components/common/Header';
import Avatar from '../../components/common/Avatar';
import StarRating from '../../components/common/StarRating';
import { InlineLoader } from '../../components/common/Loader';
import { getDoctorProfile, getDoctorSlots, getConversations } from '../../services/patientService';
import { formatTime12, todayISO } from '../../utils/helpers';
import { useAuth } from '../../context/AuthContext';

// ── helpers ────────────────────────────────────────────────────────────────
function getNext14Days() {
  return Array.from({ length: 14 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() + i);
    return d.toISOString().split('T')[0];
  });
}

function slotColor(slot) {
  const isFull  = slot.bookedCount >= slot.maxPatients;
  const hasDelay = slot.delayMinutes > 0;
  if (isFull)   return { bg:'#FEE2E2', border:'#FCA5A5', text:'#DC2626', label:'Full',                    dot:'#DC2626' };
  if (hasDelay) return { bg:'#FEF3C7', border:'#FCD34D', text:'#92400E', label:`+${slot.delayMinutes}m`,  dot:'#F59E0B' };
  return          { bg:'#ECFDF5', border:'#6EE7B7', text:'#065F46', label:'On Time',                      dot:'#10B981' };
}

// ── Date Scroller ──────────────────────────────────────────────────────────
function DateScroller({ dates, selected, onChange }) {
  const scrollRef = useRef(null);

  const isFirstMount = React.useRef(true);
  useEffect(() => {
    // Skip scrollIntoView on first mount — causes page to scroll down on open
    if (isFirstMount.current) { isFirstMount.current = false; return; }
    const idx = dates.indexOf(selected);
    if (scrollRef.current && idx >= 0) {
      const child = scrollRef.current.children[idx];
      if (child) {
        // Use scrollLeft directly to prevent any vertical page scroll
        const container  = scrollRef.current;
        const childLeft  = child.offsetLeft;
        const childWidth = child.offsetWidth;
        const contWidth  = container.offsetWidth;
        const target     = childLeft - (contWidth / 2) + (childWidth / 2);
        container.scrollTo({ left: target, behavior: 'smooth' });
      }
    }
  }, [selected, dates]);

  return (
    <div ref={scrollRef}
      className="flex gap-2 overflow-x-auto scrollbar-hide px-4 pb-2"
      style={{ scrollSnapType: 'x mandatory' }}>
      {dates.map(date => {
        const d      = new Date(date + 'T00:00:00');
        const isToday = date === todayISO();
        const isSel   = date === selected;
        const day    = d.toLocaleDateString('en', { weekday: 'short' });
        const num    = d.getDate();
        const mon    = d.toLocaleDateString('en', { month: 'short' });
        return (
          <button key={date} onClick={() => onChange(date)}
            style={{ scrollSnapAlign: 'center', minWidth: '56px' }}
            className={`flex-shrink-0 flex flex-col items-center py-2.5 px-2 rounded-2xl border-2 transition-all active:scale-95 ${
              isSel
                ? 'bg-primary border-primary shadow-md'
                : isToday
                  ? 'bg-primary/8 border-primary/40'
                  : 'bg-white border-slate-200'
            }`}>
            <span className={`text-[10px] font-bold uppercase tracking-wider ${isSel ? 'text-white/80' : isToday ? 'text-primary' : 'text-slate-400'}`}>
              {day}
            </span>
            <span className={`text-lg font-black leading-tight ${isSel ? 'text-white' : 'text-slate-800'}`}>
              {num}
            </span>
            <span className={`text-[9px] font-semibold ${isSel ? 'text-white/70' : 'text-slate-400'}`}>
              {mon}
            </span>
            {isToday && !isSel && (
              <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1" />
            )}
          </button>
        );
      })}
    </div>
  );
}

// ── Slot Grid ──────────────────────────────────────────────────────────────
function SlotGrid({ slots, selected, onSelect, type }) {
  if (slots.length === 0) return (
    <div className="flex flex-col items-center py-10 px-4">
      <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-3">
        <i className={`fa-solid ${type === 'video' ? 'fa-video' : 'fa-calendar-xmark'} text-2xl text-slate-300`} />
      </div>
      <p className="text-sm font-semibold text-slate-500">No slots available</p>
      <p className="text-xs text-slate-400 mt-1">Try selecting another date</p>
    </div>
  );

  // Group by morning / afternoon / evening
  const groups = { Morning: [], Afternoon: [], Evening: [] };
  slots.forEach(slot => {
    const [h] = slot.startTime.split(':').map(Number);
    if (h < 12)      groups.Morning.push(slot);
    else if (h < 17) groups.Afternoon.push(slot);
    else             groups.Evening.push(slot);
  });

  const groupMeta = {
    Morning:   { icon: 'fa-sun',         color: '#F59E0B', bg: '#FFFBEB' },
    Afternoon: { icon: 'fa-cloud-sun',   color: '#3B82F6', bg: '#EFF6FF' },
    Evening:   { icon: 'fa-moon',        color: '#8B5CF6', bg: '#F5F3FF' },
  };

  return (
    <div className="space-y-4 px-4 pb-4">
      {Object.entries(groups).filter(([,s]) => s.length > 0).map(([group, grpSlots]) => {
        const meta = groupMeta[group];
        return (
          <div key={group}>
            {/* Group header */}
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ background: meta.bg }}>
                <i className={`fa-solid ${meta.icon} text-[11px]`} style={{ color: meta.color }} />
              </div>
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">{group}</span>
              <span className="text-[10px] text-slate-400">({grpSlots.length} slots)</span>
            </div>
            {/* Slot pills */}
            <div className="flex flex-wrap gap-2">
              {grpSlots.map(slot => {
                const c      = slotColor(slot);
                const isSel  = selected?._id === slot._id;
                const isFull = slot.bookedCount >= slot.maxPatients;
                return (
                  <button key={slot._id}
                    disabled={isFull}
                    onClick={() => !isFull && onSelect(slot)}
                    className="flex flex-col items-center rounded-xl border-2 px-3 py-2 transition-all active:scale-95"
                    style={{
                      background:   isSel ? '#0D9488' : c.bg,
                      borderColor:  isSel ? '#0D9488' : c.border,
                      opacity:      isFull ? 0.55 : 1,
                      cursor:       isFull ? 'not-allowed' : 'pointer',
                      minWidth:     '72px',
                    }}>
                    <span className="text-sm font-black" style={{ color: isSel ? '#fff' : '#0F172A' }}>
                      {formatTime12(slot.startTime)}
                    </span>
                    <div className="flex items-center gap-1 mt-0.5">
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: isSel ? 'rgba(255,255,255,0.7)' : c.dot }} />
                      <span className="text-[9px] font-semibold" style={{ color: isSel ? 'rgba(255,255,255,0.85)' : c.text }}>
                        {c.label}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Info Row ──────────────────────────────────────────────────────────────
function InfoRow({ icon, label, value, color = 'text-primary' }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-border last:border-0">
      <div className="w-8 h-8 rounded-lg bg-primary-light flex items-center justify-center flex-shrink-0">
        <i className={`fa-solid ${icon} ${color} text-sm`} />
      </div>
      <div className="flex-1">
        <p className="text-[10px] text-text-muted uppercase tracking-wide font-medium">{label}</p>
        <p className="text-sm text-text-primary font-medium mt-0.5">{value}</p>
      </div>
    </div>
  );
}

// ── Legend pill ────────────────────────────────────────────────────────────
function LegendPill({ dot, label }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="w-2 h-2 rounded-full" style={{ background: dot }} />
      <span className="text-[10px] text-slate-500 font-medium">{label}</span>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────
export default function DoctorProfilePage() {
  const { id }         = useParams();
  const navigate       = useNavigate();
  const { isLoggedIn } = useAuth();
  // Holds this doctor's video conversation (if patient has booked video consult before/now)
  const [videoConv, setVideoConv] = useState(null);

  const [doctor, setDoctor]   = useState(null);
  const [reviews, setReviews] = useState([]);
  const [slots, setSlots]     = useState({ clinic: [], video: [] });
  const [selDate, setSelDate] = useState(todayISO());
  const [selSlot, setSelSlot] = useState(null);
  const [selType, setSelType] = useState('clinic');
  const [loading, setLoading] = useState(true);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [showFullBio, setShowFullBio] = useState(false);
  const [activeImg, setActiveImg] = useState(null);
  const dates = getNext14Days();

  const loadDoctor = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getDoctorProfile(id);
      setDoctor(res.doctor);
      setReviews(res.reviews || []);
    } catch {
      toast.error('Doctor not found');
      navigate(-1);
    } finally { setLoading(false); }
  }, [id, navigate]);

  // Check if patient already has a video consultation conversation with this doctor
  // (shows Chat button — open even after slot expires, for reading old messages)
  const loadVideoConv = useCallback(async () => {
    if (!isLoggedIn) return;
    try {
      const res   = await getConversations();
      const convs = res.conversations || res.patientConvs || res.data || [];
      const match = convs.find(c =>
        c.appointmentType === 'video' &&
        (c._id || '').includes(id)
      );
      setVideoConv(match || null);
    } catch {
      setVideoConv(null);
    }
  }, [id, isLoggedIn]);

  const loadSlots = useCallback(async (slotDate) => {
    setSlotsLoading(true);
    try {
      // FIX: only load the type currently selected — don't pre-load both
      // This ensures video slot counts don't decrease when booking clinic visit
      const clinicRes = await getDoctorSlots(id, slotDate, 'clinic');
      const videoRes  = (doctor?.videoConsultEnabled && selType === 'video')
        ? await getDoctorSlots(id, slotDate, 'video')
        : { slots: [] };
      setSlots({ clinic: clinicRes.slots || [], video: videoRes.slots || [] });
      setSelSlot(null);
    } catch {/* silent */}
    finally { setSlotsLoading(false); }
  }, [id, selType, doctor?.videoConsultEnabled]); // date is a param, not state

  useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' }); }, []); // scroll to top on mount only
  useEffect(() => { loadDoctor(); }, [loadDoctor]);
  useEffect(() => { loadVideoConv(); }, [loadVideoConv]);
  useEffect(() => { if (doctor) loadSlots(selDate); }, [doctor, selDate, loadSlots]);

  const handleBook = () => {
    if (!selSlot) { toast.error('Please select a slot first'); return; }
    if (!isLoggedIn) {
      toast('Please login to book your appointment');
      // Save intended booking in sessionStorage so we can resume after login
      sessionStorage.setItem('pendingBooking', JSON.stringify({ doctorId: id, slotId: selSlot._id, type: selType }));
      navigate('/login', { state: { from: `/doctor/${id}` } });
      return;
    }
    navigate(`/book/${id}`, { state: { slot: selSlot, doctor, type: selType } });
  };

  if (loading) return (
    <div className="page-container-no-bottom"><Header showBack showLogo /><InlineLoader /></div>
  );
  if (!doctor) return null;

  const bio       = doctor.bio || '';
  const hasQuals  = doctor.qualifications?.length > 0;
  const totalExp  = doctor.qualifications?.reduce((s, q) => s + (Number(q.experience) || 0), 0);
  const hasMed    = !!(doctor.medicineStaffName || doctor.medicineStaffNumber);
  const heroImg   = doctor.clinicImages?.[0] || null;
  const activeSlots = selType === 'clinic' ? slots.clinic : slots.video;
  const availCount  = activeSlots.filter(s => s.bookedCount < s.maxPatients).length;

  return (
    <div className="page-container-no-bottom pb-36">
      <Header showBack showLogo />

      {/* Hero image */}
      {heroImg && (
        <div className="w-full h-48 bg-primary-light overflow-hidden">
          <img src={heroImg} alt={doctor.clinicName} className="w-full h-full object-cover" />
        </div>
      )}

      {/* ── Doctor Card ──────────────────────────────────── */}
      <div className="bg-white px-4 pt-4 pb-4 border-b border-border">
        <div className="flex gap-3 items-start">
          <div className="relative flex-shrink-0">
            <Avatar src={doctor.profileImg} name={doctor.name} size="lg" />
            {doctor.isVerified && (
              <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-primary rounded-full flex items-center justify-center border-2 border-white">
                <i className="fa-solid fa-check text-white text-[8px]" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold text-text-primary font-heading leading-tight">{doctor.name}</h1>
            <p className="text-sm text-primary font-semibold">{doctor.speciality}</p>
            {hasQuals && (
              <p className="text-xs text-text-muted mt-0.5">
                {doctor.qualifications.map(q => q.degree).filter(Boolean).join(' · ')}
              </p>
            )}
            <div className="flex items-center gap-3 mt-1.5 flex-wrap">
              {totalExp > 0 && (
                <span className="text-xs text-text-secondary font-medium">
                  <i className="fa-solid fa-briefcase-medical text-primary mr-1 text-[10px]" />
                  {totalExp}+ yrs
                </span>
              )}
              <StarRating rating={doctor.rating} count={doctor.reviewCount} size="sm" />
            </div>
          </div>
        </div>

        <div className="mt-3 flex items-center gap-2 flex-wrap">
          {doctor.clinicName && (
            <span className="flex items-center gap-1.5 bg-primary-light text-primary text-xs font-semibold px-3 py-1.5 rounded-full">
              <i className="fa-solid fa-hospital text-[10px]" />{doctor.clinicName}
            </span>
          )}
          {doctor.location?.city && (
            <span className="flex items-center gap-1.5 bg-surface text-text-secondary text-xs px-3 py-1.5 rounded-full border border-border">
              <i className="fa-solid fa-location-dot text-primary text-[10px]" />{doctor.location.city}
            </span>
          )}
        </div>

        {bio && (
          <div className="mt-3">
            <p className="text-sm text-text-secondary leading-relaxed">
              {showFullBio || bio.length <= 130 ? bio : bio.slice(0, 130) + '…'}
            </p>
            {bio.length > 130 && (
              <button onClick={() => setShowFullBio(!showFullBio)}
                className="text-xs text-primary font-semibold mt-1">
                {showFullBio ? 'Show less ↑' : 'Read more ↓'}
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── Quick stats ───────────────────────────────────── */}
      <div className="grid grid-cols-3 gap-2 px-4 py-3 bg-white mt-2 border-b border-border">
        <div className="text-center p-2.5 rounded-2xl bg-teal-50">
          <p className="text-lg font-black text-teal-700 font-heading">₹{doctor.consultationFee || 0}</p>
          <p className="text-[10px] text-teal-600 font-semibold">Fee</p>
        </div>
        <div className="text-center p-2.5 rounded-2xl bg-amber-50">
          <p className="text-lg font-black text-amber-700 font-heading">
            {Number(doctor.rating || 0).toFixed(1)}★
          </p>
          <p className="text-[10px] text-amber-600 font-semibold">{doctor.reviewCount || 0} Reviews</p>
        </div>
        <div className="text-center p-2.5 rounded-2xl bg-blue-50">
          <p className="text-lg font-black text-blue-700 font-heading">
            {totalExp > 0 ? `${totalExp}+` : 'New'}
          </p>
          <p className="text-[10px] text-blue-600 font-semibold">Yrs Exp</p>
        </div>
      </div>

      {/* ── Clinic Info ───────────────────────────────────── */}
      <div className="bg-white mt-2 px-4 py-2 border-b border-border">
        <p className="text-sm font-bold text-text-primary font-heading py-2">Clinic Information</p>
        <InfoRow icon="fa-hospital"       label="Clinic"       value={doctor.clinicName} />
        {/* Clickable location — opens Google Maps */}
        {(doctor.location?.address || doctor.location?.city || doctor.location?.coordinates?.lat) && (() => {
          const lat  = doctor.location?.coordinates?.lat;
          const lng  = doctor.location?.coordinates?.lng;
          const text = [doctor.location?.address, doctor.location?.city].filter(Boolean).join(', ');
          const mapsUrl = lat && lng
            ? `https://www.google.com/maps?q=${lat},${lng}`
            : `https://www.google.com/maps/search/${encodeURIComponent(text)}`;
          return (
            <a href={mapsUrl} target="_blank" rel="noopener noreferrer"
              className="flex items-start gap-3 py-2.5 border-b border-border last:border-0 group">
              <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
                <i className="fa-solid fa-location-dot text-red-500 text-sm" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] text-text-muted uppercase tracking-wide font-medium">Location</p>
                <p className="text-sm text-primary font-semibold mt-0.5 group-hover:underline">
                  {text || 'View on map'}
                </p>
                <p className="text-[10px] text-text-muted mt-0.5 flex items-center gap-1">
                  <i className="fa-solid fa-map text-[9px]" />
                  Tap to open in Google Maps
                </p>
              </div>
              <i className="fa-solid fa-arrow-up-right-from-square text-text-muted text-xs mt-2 flex-shrink-0" />
            </a>
          );
        })()}
        <InfoRow icon="fa-indian-rupee-sign" label="Fee"       value={`₹${doctor.consultationFee || 0}`} />
        {doctor.cancellationCharge > 0 && (
          <InfoRow icon="fa-triangle-exclamation" label="Cancellation" color="text-accent"
            value={`₹${doctor.cancellationCharge} deducted if you cancel`} />
        )}
        {doctor.oldPatientDiscount?.enabled && doctor.oldPatientDiscount?.value > 0 && (
          <InfoRow icon="fa-tag" label="Returning Patient Discount" color="text-success"
            value={doctor.oldPatientDiscount.type === 'percent'
              ? `${doctor.oldPatientDiscount.value}% off on revisit`
              : `₹${doctor.oldPatientDiscount.value} off on revisit`} />
        )}
        {doctor.videoConsultEnabled && (
          <InfoRow icon="fa-video" label="Video Consult" value="Available · Book an online slot" color="text-info" />
        )}
      </div>

      {/* ── Chat with Doctor (only if patient has a video consultation booking) ── */}
      {videoConv && (
        <div className="bg-white mt-2 px-4 py-3 border-b border-border">
          <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-2xl border border-blue-200">
            <div className="w-12 h-12 bg-info rounded-xl flex items-center justify-center flex-shrink-0">
              <i className="fa-solid fa-comment-medical text-white text-lg" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-text-primary">Chat with {doctor.name || 'Doctor'}</p>
              <p className="text-xs text-blue-700 font-medium">
                {videoConv.isTimeLocked === false || videoConv.appointmentStatus === 'completed'
                  ? 'View your conversation history'
                  : 'Available during your video slot'}
              </p>
            </div>
            <button onClick={() => {
              navigate(`/chat/${videoConv._id}`, {
                state: {
                  appointmentId: videoConv.appointmentId || null,
                  doctorName:    doctor.name || '',
                  doctorImg:     doctor.profileImage || null,
                }
              });
            }} className="flex items-center gap-2 bg-info text-white text-xs font-bold px-4 py-2.5 rounded-xl active:scale-95 transition-transform flex-shrink-0">
              <i className="fa-solid fa-comment-dots text-sm" />Chat
            </button>
          </div>
        </div>
      )}

      {/* ── Qualifications ────────────────────────────────── */}
      {hasQuals && (
        <div className="bg-white mt-2 px-4 py-2 border-b border-border">
          <p className="text-sm font-bold text-text-primary font-heading py-2">Education & Experience</p>
          {doctor.qualifications.map((q, i) => (
            <div key={i} className="flex gap-3 py-2.5 border-b border-border last:border-0">
              <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="fa-solid fa-graduation-cap text-info text-sm" />
              </div>
              <div>
                <p className="text-sm font-semibold text-text-primary">{q.degree}</p>
                {q.specialization && <p className="text-xs text-primary">{q.specialization}</p>}
                {q.college && <p className="text-xs text-text-muted">{q.college}</p>}
                {q.experience > 0 && (
                  <span className="inline-block mt-1 text-[10px] bg-green-50 text-green-700 px-2 py-0.5 rounded-full font-semibold">
                    {q.experience} yrs experience
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Medicine Staff ────────────────────────────────── */}
      {hasMed && (
        <div className="bg-white mt-2 px-4 py-3 border-b border-border">
          <p className="text-sm font-bold text-text-primary font-heading mb-3">Medicine & Pharmacy</p>
          <div className="flex items-center gap-3 p-3 bg-green-50 rounded-2xl border border-green-200">
            <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center flex-shrink-0">
              <i className="fa-solid fa-pills text-white text-lg" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-text-primary">{doctor.medicineStaffName || 'Medicine Store'}</p>
              <p className="text-xs text-green-700 font-medium">Order medicines · Prescriptions · No time limit</p>
            </div>
            <button onClick={() => {
              if (!isLoggedIn) { toast('Login required'); sessionStorage.setItem('loginOrigin', window.location.pathname); navigate('/login', { state: { from: window.location.pathname } }); return; }
              navigate(`/medicine-chat/${doctor._id}`, {
                state: { staffName: doctor.medicineStaffName || 'Medicine Store' }
              });
            }} className="flex items-center gap-2 bg-green-600 text-white text-xs font-bold px-4 py-2.5 rounded-xl active:scale-95 transition-transform flex-shrink-0">
              <i className="fa-solid fa-comment-dots text-sm" />Chat
            </button>
          </div>
        </div>
      )}

      {/* ── Clinic Photos ─────────────────────────────────── */}
      {doctor.clinicImages?.length > 0 && (
        <div className="bg-white mt-2 px-4 py-3 border-b border-border">
          <p className="text-sm font-bold text-text-primary font-heading mb-3">Clinic Photos</p>
          <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1">
            {doctor.clinicImages.map((img, i) => (
              <button key={i} onClick={() => setActiveImg(img)}
                className="flex-shrink-0 w-28 h-28 rounded-2xl overflow-hidden border border-border">
                <img src={img} alt={'clinic '+i} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════
          SLOT SECTION — premium redesign
          ════════════════════════════════════════════════════ */}
      <div className="bg-white mt-2 border-b border-border">

        {/* Section header */}
        <div className="flex items-center justify-between px-4 pt-4 pb-2">
          <div>
            <p className="text-base font-bold text-text-primary font-heading">Book a Slot</p>
            <p className="text-xs text-text-muted mt-0.5">Scroll to pick your date</p>
          </div>
          {availCount > 0 && (
            <span className="bg-green-100 text-green-700 text-xs font-bold px-2.5 py-1 rounded-full">
              {availCount} available
            </span>
          )}
        </div>

        {/* Date scroller */}
        <DateScroller dates={dates} selected={selDate} onChange={d => { setSelDate(d); setSelSlot(null); }} />

        {/* Type tabs (only if video enabled) */}
        {doctor.videoConsultEnabled && (
          <div className="flex gap-2 px-4 pt-2 pb-3">
            {[
              { key:'clinic', icon:'fa-hospital', label:'Clinic Visit' },
              { key:'video',  icon:'fa-video',    label:'Video Consult' },
            ].map(t => (
              <button key={t.key} onClick={() => { setSelType(t.key); setSelSlot(null); }}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl border-2 text-xs font-bold transition-all ${
                  selType === t.key
                    ? t.key === 'video'
                      ? 'bg-blue-500 border-blue-500 text-white'
                      : 'bg-primary border-primary text-white'
                    : 'bg-white border-slate-200 text-slate-500'
                }`}>
                <i className={`fa-solid ${t.icon} text-sm`} />{t.label}
              </button>
            ))}
          </div>
        )}

        {/* Legend */}
        <div className="flex items-center gap-4 px-4 pb-3">
          <LegendPill dot="#10B981" label="On Time" />
          <LegendPill dot="#F59E0B" label="Delayed" />
          <LegendPill dot="#DC2626" label="Full" />
        </div>

        {/* Slots */}
        {slotsLoading
          ? <div className="py-8"><InlineLoader text="Loading slots…" /></div>
          : <SlotGrid slots={activeSlots} selected={selSlot} onSelect={setSelSlot} type={selType} />
        }
      </div>

      {/* ── Reviews ───────────────────────────────────────── */}
      <div className="bg-white mt-2 px-4 pt-3 pb-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-bold text-text-primary">
            Patient Reviews {doctor.reviewCount > 0 && `(${doctor.reviewCount})`}
          </p>
          {doctor.rating > 0 && (
            <div className="flex items-center gap-1.5 bg-amber-50 px-3 py-1 rounded-full">
              <i className="fa-solid fa-star text-amber-400 text-xs" />
              <span className="text-sm font-bold text-amber-700">{Number(doctor.rating).toFixed(1)}</span>
            </div>
          )}
        </div>
        {reviews.length === 0 ? (
          <div className="text-center py-6">
            <i className="fa-solid fa-comment-medical text-2xl text-text-muted mb-2 block" />
            <p className="text-sm text-text-muted">No reviews yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {reviews.slice(0, 5).map(r => (
              <div key={r._id} className="p-3 bg-surface rounded-2xl border border-border">
                <div className="flex items-center gap-2 mb-1.5">
                  <Avatar src={r.patientId?.profileImg} name={r.patientId?.name} size="xs" />
                  <span className="text-xs font-semibold text-text-primary">{r.patientId?.name || 'Patient'}</span>
                  <StarRating rating={r.rating} showCount={false} />
                  <span className="text-[10px] text-text-muted ml-auto">
                    {r.createdAt ? new Date(r.createdAt).toLocaleDateString('en', { day:'numeric', month:'short' }) : ''}
                  </span>
                </div>
                {r.comment && <p className="text-xs text-text-secondary leading-relaxed">{r.comment}</p>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Full-screen image viewer */}
      {activeImg && (
        <div className="fixed inset-0 bg-black z-50 flex items-center justify-center"
          onClick={() => setActiveImg(null)}>
          <img src={activeImg} alt="clinic" className="max-w-full max-h-full object-contain" />
          <button className="absolute top-4 right-4 w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
            <i className="fa-solid fa-xmark text-white text-lg" />
          </button>
        </div>
      )}

      {/* ── Sticky Book Bar ───────────────────────────────── */}
      <div className="fixed bottom-0 left-0 right-0 z-30"
        style={{ background:'#fff', borderTop:'1px solid #E2E8F0', paddingBottom:'env(safe-area-inset-bottom,0px)' }}>
        <div className="px-4 py-3">
          <div className="flex items-center justify-between mb-2.5">
            <div>
              <p className="text-[10px] text-text-muted uppercase tracking-wide font-semibold">Consult Fee</p>
              <p className="text-xl font-black text-text-primary">₹{doctor.consultationFee || 0}</p>
            </div>
            {selSlot ? (
              <div className="text-right">
                <p className="text-[10px] text-text-muted">Selected</p>
                <p className="text-base font-bold text-primary">{formatTime12(selSlot.startTime)}</p>
                <p className="text-[10px] text-text-muted">{new Date(selDate+'T00:00:00').toLocaleDateString('en',{weekday:'short',day:'numeric',month:'short'})}</p>
              </div>
            ) : (
              <div className="text-right">
                <p className="text-xs text-text-muted">{availCount} slot{availCount !== 1 ? 's' : ''} free</p>
                <p className="text-[10px] text-text-muted">Pick a time above</p>
              </div>
            )}
          </div>
          <button onClick={handleBook} disabled={!selSlot}
            className={`w-full py-3.5 rounded-2xl text-sm font-bold transition-all active:scale-98 ${
              selSlot
                ? 'bg-primary text-white shadow-lg shadow-primary/30'
                : 'bg-slate-100 text-slate-400 cursor-not-allowed'
            }`}>
            {selSlot
              ? <span><i className="fa-solid fa-calendar-check mr-2" />Book {formatTime12(selSlot.startTime)} · {selType === 'video' ? 'Video' : 'Clinic'}</span>
              : 'Select a slot to continue'}
          </button>
        </div>
      </div>
    </div>
  );
}
